/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form");
      this.optionSelectors = this.querySelectorAll(
        ".option-selector:not(.option-selector--custom)"
      );
      this.nativeSelector = document.getElementById(
        `variants-${this.section.dataset.section}`
      );
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener("change", this.handleVariantChange.bind(this));
      this.applySearchParams();

      this.setAttribute("loaded", "");
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const previousVariant = this.variant;
      this.selectedOptions = this.getSelectedOptions();
      this.resolveOptionChange(evt, previousVariant);
      this.variant = this.getSelectedVariant();

      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions,
          },
        })
      );
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn =
        this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant
        ? theme.strings.noStock
        : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;
      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);
      // Check if the element has the 'is-unavailable' class
      if (el.classList.contains("is-unavailable")) {
        el.style.display = "none"; // Hide the element if it's unavailable

        // Hide the next sibling <label> if it exists
        const nextLabel = el.nextElementSibling;
        if (nextLabel && nextLabel.tagName === "LABEL") {
          nextLabel.style.display = "none";
        }
      } else {
        el.style.display = ""; // Show the element if it's not unavailable
        // Show the next sibling <label> if it exists
        const nextLabel = el.nextElementSibling;
        if (nextLabel && nextLabel.tagName === "LABEL") {
          nextLabel.style.display = "";
        }
      }

      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        //  el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }

      // Automatically click the first available option
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'

      console.log("The dataset ", this.dataset);
      if (availabilityMode === "selection") {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          selector.querySelectorAll('.js-option').forEach((option) => {
            const value = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            const matches = this.data.variants.filter((candidate) =>
              candidate.options[selectorIndex] === value && (candidate.options.every((candidateValue, index) =>
                index === selectorIndex || this.selectedOptions[index] === null || candidateValue === this.selectedOptions[index]
              ) || (/prescription/i.test(selector.dataset.option || '') && this.variant?.barcode && candidate.barcode === this.variant.barcode))
            );
            VariantPicker.updateOptionAvailability(option, matches.length > 0, matches.some((candidate) => candidate.available));
          });
        });
        return;
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])'
          );
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i] === this.selectedOptions[i] ||
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable
            );
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder =
        this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product"
          );
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price =
        this.price || this.section.querySelector(".product-price .price");

      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price)
            variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        if (priceWasEl) {
          priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || "";
        }

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle(
          "price--on-sale",
          variant.compare_at_price > variant.price
        );
        this.price.classList.toggle(
          "price--sold-out",
          !variant.available && !this.preSelection
        );
      }

      this.price.querySelector(".price__default").hidden =
        !this.variant && !this.preSelection;
      const noVariantPrice = this.price.querySelector(".price__no-variant");
      if (noVariantPrice) {
        noVariantPrice.hidden = this.variant || this.preSelection;
      }
      const from = this.price.querySelector(".price__from");
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode =
        this.barcode || this.section.querySelector(".product-barcode__value");
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;
      const url = new URL(this.dataset.url, window.location.origin);
      url.search = window.location.search;
      if (this.variant) url.searchParams.set('variant', this.variant.id);
      else url.searchParams.delete('variant');
      window.history.replaceState({}, "", url.toString());
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      // Apps may create/replace forms after their initial asynchronous load.
      this.forms = this.section.querySelectorAll(".js-product-form, .js-instalments-form");

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        if (!input) return;
        const nextValue = this.variant ? String(this.variant.id) : "";
        if (input.value !== nextValue) {
          input.value = nextValue;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    resolveOptionChange(evt, previousVariant) {
      if (this.data.variants.some((v) => v.options.every((value, index) => value === this.selectedOptions[index]))) return;
      const changedSelector = evt?.target?.closest('.option-selector');
      const changedIndex = Array.from(this.optionSelectors).indexOf(changedSelector);
      if (changedIndex < 0 || !/prescription/i.test(changedSelector.dataset.option || '')) return;
      const candidates = this.data.variants.filter((v) => v.options[changedIndex] === this.selectedOptions[changedIndex]);
      // Factory and custom Rx lens labels can differ; a shared UPC still
      // identifies the same frame. Never infer a pairing by list position.
      const sameUPC = previousVariant?.barcode && candidates.filter((v) => v.barcode === previousVariant.barcode);
      const paired = sameUPC?.length === 1 ? sameUPC[0] : null;
      if (!paired || Array.from(this.optionSelectors).some((s) => s.dataset.selectorType === 'dropdown')) return;
      this.optionSelectors.forEach((selector, index) => {
        selector.querySelectorAll('input.js-option').forEach((input) => { input.checked = input.value === paired.options[index]; });
      });
      this.selectedOptions = this.getSelectedOptions();
      this.optionSelectors.forEach((selector, index) => {
        const label = selector.querySelector('.option-selector__label-value');
        if (label) label.textContent = this.selectedOptions[index] || '';
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selected = selector.querySelector(
            '.custom-select__option[aria-selected="true"]'
          );
          selectedOptions.push(
            selected && selected.dataset.value !== ""
              ? selected.dataset.value
              : null
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    // Function to collect engraving data from the script tag
    collectEngravingData() {
      const engravingDataElement = document.querySelector(
        ".js-engraving-data-variant-media"
      );

      if (engravingDataElement) {
        const textContent = engravingDataElement.textContent.trim();

        if (textContent) {
          try {
            // Attempt to parse the JSON
            const parsedData = JSON.parse(textContent);

            // Validate that the data has the expected structure
            if (
              parsedData &&
              (Array.isArray(parsedData.variants) ||
                parsedData.variants === undefined) &&
              (parsedData.product_option ||
                parsedData.product_option === undefined)
            ) {
              return parsedData;
            } else {
              console.warn(
                "Engraving data does not have the expected structure."
              );
              return null;
            }
          } catch (error) {
            console.error("Failed to parse engraving data:", error);
            return null;
          }
        } else {
          console.warn("Engraving data script tag is empty.");
          return null;
        }
      } else {
        console.warn("Engraving data script tag not found.");
        return null;
      }
    }

    setEngravingImage(variant) {
      const preview = document.querySelector(".engraving-preview");
      const source = document.querySelector("#engraving-meta-image");
      if (!preview || !source?.getAttribute("src")) return;
      const image = document.createElement("img");
      image.src = source.src;
      image.alt = variant.title + " Engraving Preview";
      preview.querySelector("img")?.remove();
      preview.prepend(image);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const variant = this.data.variants.find((v) =>
        v.options.every((value, index) => value === this.selectedOptions[index])
      );
      const root = this.section;
      const normalizedTitle = variant?.title.replace(/ /g, "");
      const groups = [
        ["vision-", "VisionColor"],
        ["spectrum-", "SpectrumReport"],
        ["lens-", "Lenses"],
        ["prescription-", "PrescriptionInsert"]
      ];
      groups.forEach(([prefix, accordionClass]) => {
        const accordion = root.querySelector("." + accordionClass);
        if (!accordion) return;
        // Scope metadata to this product and inspect all class tokens, not
        // only the first token in a potentially reordered class attribute.
        const candidates = [...accordion.querySelectorAll("[class]")].filter((el) =>
          [...el.classList].some((name) => name.startsWith(prefix))
        );
        const matches = candidates.filter((el) => normalizedTitle && el.classList.contains(prefix + normalizedTitle));
        candidates.forEach((el) => { el.style.display = matches.includes(el) ? "block" : "none"; });
        if (accordionClass === "PrescriptionInsert") {
          const fallback = accordion.querySelector(".prescription_default");
          const hasSpecific = matches.some((el) => el.querySelector("img") || el.textContent.trim());
          if (fallback) fallback.style.display = hasSpecific ? "none" : "block";
          const nonPrescription = variant?.options.some((option) => /^non[- ]?prescription$/i.test(option));
          accordion.style.display = !variant || nonPrescription ? "none" : "block";
        } else {
          const visibleImages = [...accordion.querySelectorAll("img")].some((img) => {
            for (let node = img; node && node !== accordion; node = node.parentElement) {
              if (node.style.display === 'none') return false;
            }
            return true;
          });
          accordion.style.display = variant && visibleImages ? "block" : "none";
        }
      });
      clearTimeout(this.engravingTimer);
      if (variant) {
        this.engravingTimer = setTimeout(() => {
          if (this.variant?.id === variant.id) this.setEngravingImage(variant);
        }, 3000);
      }
      return variant;
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find(
          (x) => x.dataset.option === key
        );
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === "dropdown") {
          const colorOptionDropdown = Array.from(matchingOptionSelector.querySelectorAll('.custom-select__option'))
            .find((option) => option.dataset.value === value);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest("custom-select");
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = Array.from(matchingOptionSelector.querySelectorAll('input'))
            .find((input) => input.value === value);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector(
              ".option-selector__label-value"
            );
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector(
                ".option-selector__label-value"
              ).textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent("change", { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
